import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2, Check, X, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { avatarSrc } from "@/lib/defaults";
import { VerifiedBadge } from "@/components/VerifiedBadge";

export const FOLLOW_REQUESTS_EVENT = "yalola:follow-requests";
export const FOLLOW_REQUESTS_LINK = "/settings?section=confidentialite&focus=settings-profile-privacy-card";
const emitRequests = (count) => window.dispatchEvent(new CustomEvent(FOLLOW_REQUESTS_EVENT, { detail: { count } }));

// Boutons Accepter / Refuser d'une notification « X souhaite s'abonner à toi » (cloche + page Notifications).
// `onDone(patch)` reçoit le nouveau meta de la notification (kind follow_request_done + result).
export const FollowRequestNotifActions = ({ n, onDone, testPrefix = "notif" }) => {
  const { t } = useApp();
  const [busy, setBusy] = useState(false);
  const kind = n.meta?.kind;
  if (kind === "follow_request_done") {
    return (
      <div className="text-xs mt-1 font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid={`${testPrefix}-follow-request-done-${n.id}`}>
        {n.meta?.result === "declined" ? t("privacy.declined_toast", "Demande refusée") : t("privacy.request_handled", "Demande traitée")}
      </div>
    );
  }
  if (kind !== "follow_request" || !n.meta?.requester_id) return null;
  const act = async (e, action) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post(`/users/me/follow-requests/${n.meta.requester_id}/${action}`);
      emitRequests(data.pending_requests || 0);
      onDone?.({ kind: "follow_request_done", result: action === "accept" ? "accepted" : "declined" });
      if (action === "accept") toast.success(t("privacy.accepted_toast", "{name} fait maintenant partie de tes abonnés").replace("{name}", n.actor?.name || ""));
      else toast.success(t("privacy.declined_toast", "Demande refusée"));
    } catch (err) {
      if (err?.response?.status === 404) onDone?.({ kind: "follow_request_done" });
      else toast.error(err?.friendlyMessage || t("common.error", "Erreur"));
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap gap-2 mt-2" onClick={(e) => e.stopPropagation()} data-testid={`${testPrefix}-follow-request-${n.id}`}>
      <Button size="sm" className="pill-primary rounded-full h-8" disabled={busy} onClick={(e) => act(e, "accept")} data-testid={`${testPrefix}-follow-accept-${n.id}`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} className="mr-1" /> {t("privacy.accept", "Accepter")}</>}
      </Button>
      <Button size="sm" variant="outline" className="rounded-full h-8" disabled={busy} onClick={(e) => act(e, "decline")} data-testid={`${testPrefix}-follow-decline-${n.id}`}>
        <X size={14} className="mr-1" /> {t("privacy.decline", "Refuser")}
      </Button>
    </div>
  );
};

// Liste des demandes d'abonnement du membre (profil privé) : Accepter / Refuser.
export const FollowRequestsList = ({ onCount, compact = false }) => {
  const { t } = useApp();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/users/me/follow-requests").then(({ data }) => { setItems(data.requests || []); onCount?.(data.count || 0); }).catch(() => setItems([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (r, action) => {
    setBusy(r.id);
    try {
      const { data } = await api.post(`/users/me/follow-requests/${r.user.id}/${action}`);
      setItems((arr) => (arr || []).filter((x) => x.id !== r.id));
      onCount?.(data.pending_requests || 0);
      emitRequests(data.pending_requests || 0);
      if (action === "accept") toast.success(t("privacy.accepted_toast", "{name} fait maintenant partie de tes abonnés").replace("{name}", r.user.name || ""));
      else toast.success(t("privacy.declined_toast", "Demande refusée"));
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || t("common.error", "Erreur")); }
    finally { setBusy(null); }
  };
  if (items === null) return <div className="py-4 grid place-items-center"><Loader2 size={18} className="animate-spin" style={{ color: "var(--yalola-text-muted)" }} /></div>;
  if (items.length === 0) return <div className="py-4 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="follow-requests-empty">{t("privacy.requests_empty", "Aucune demande en attente.")}</div>;
  return (
    <div className="space-y-1" data-testid="follow-requests-list">
      {items.map((r) => (
        <div key={r.id} className="p-3 rounded-xl" style={{ background: compact ? undefined : "var(--yalola-surface-alt)" }} data-testid={`follow-request-${r.user.id}`}>
          <Link to={`/profile/${r.user.id}`} className="flex items-center gap-3 min-w-0">
            <Avatar className="h-10 w-10 shrink-0"><AvatarImage src={avatarSrc(r.user)} /><AvatarFallback>{r.user.name?.[0]}</AvatarFallback></Avatar>
            <div className="min-w-0">
              <div className="text-sm font-semibold flex items-center gap-1" data-testid={`follow-request-name-${r.user.id}`}>{r.user.name}<VerifiedBadge user={r.user} size={12} /></div>
              {r.user.city && <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{r.user.city}</div>}
            </div>
          </Link>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button size="sm" className="pill-primary rounded-full h-9" disabled={busy === r.id} onClick={() => act(r, "accept")} data-testid={`follow-request-accept-${r.user.id}`}>
              {busy === r.id ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} className="mr-1" /> {t("privacy.accept", "Accepter")}</>}
            </Button>
            <Button size="sm" variant="outline" className="rounded-full h-9" disabled={busy === r.id} onClick={() => act(r, "decline")} data-testid={`follow-request-decline-${r.user.id}`}>
              <X size={14} className="mr-1" /> {t("privacy.decline", "Refuser")}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Carte Réglages › Confidentialité : interrupteur « Profil privé » + demandes en attente.
export const ProfilePrivacyCard = () => {
  const { t, refreshMe } = useApp();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  useEffect(() => {
    api.get("/users/me/privacy").then(({ data }) => { setState(!!data.is_private); setCount(data.pending_requests || 0); }).catch(() => setState(false));
  }, []);
  const toggle = async (on) => {
    if (busy || state === null) return;
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me/privacy", { is_private: on });
      setState(!!data.is_private);
      if (!data.is_private) {
        setCount(0); emitRequests(0);
        toast.success(data.accepted > 0
          ? t("privacy.private_back_public", "Profil public : {n} demande(s) en attente acceptée(s) automatiquement.").replace("{n}", String(data.accepted))
          : t("privacy.saved", "Réglage enregistré"));
      } else toast.success(t("privacy.saved", "Réglage enregistré"));
      refreshMe?.();
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };
  return (
    <div className="yalola-card p-5" id="profil-prive" data-testid="settings-profile-privacy-card" data-private={state ? "1" : "0"}>
      <div className="flex items-center gap-2 mb-1">
        <Lock size={18} style={{ color: "var(--yalola-primary)" }} />
        <div className="font-bold font-display text-lg">{t("privacy.private_title", "Profil privé")}</div>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--yalola-text-muted)" }}>
        {t("privacy.private_desc", "Quand ton profil est privé, chaque nouvel abonné doit être accepté par toi, et tes publications, reels et stories ne sont visibles que par tes abonnés. Tes abonnés actuels sont conservés.")}
      </p>
      <label className="flex items-center justify-between gap-3 rounded-xl border p-3 cursor-pointer" style={{ borderColor: state ? "var(--yalola-primary)" : "var(--yalola-border)" }} data-testid="profile-privacy-row">
        <div className="min-w-0">
          <div className="text-sm font-semibold">{t("privacy.private_toggle", "Rendre mon profil privé")}</div>
          <div className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }} data-testid="profile-privacy-hint">
            {state ? t("privacy.private_on_hint", "Profil privé activé : les nouvelles demandes de suivi attendent ton accord.")
              : t("privacy.private_off_hint", "Profil public (réglage par défaut) : tout le monde peut te suivre et voir tes publications.")}
          </div>
        </div>
        {busy ? <Loader2 size={18} className="animate-spin shrink-0" /> : <Switch checked={!!state} disabled={state === null} onCheckedChange={toggle} data-testid="profile-privacy-switch" />}
      </label>
      {state && (
        <div className="mt-4" data-testid="profile-privacy-requests">
          <div className="text-sm font-semibold flex items-center gap-2 mb-2"><UserCheck size={15} style={{ color: "var(--yalola-primary)" }} /> {t("privacy.requests_title", "Demandes de suivi")}{count > 0 && <span className="chip text-[10px]" data-testid="profile-privacy-requests-count">{count}</span>}</div>
          <FollowRequestsList onCount={setCount} />
        </div>
      )}
    </div>
  );
};
